import type { Session } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';
import { withBrowserAuthMutation } from '@/lib/browserAuthMutationGate';

export const WEB_SIGN_OUT_FENCE_KEY = 'th_web_sign_out_fence_v1';
export const WEB_SIGN_OUT_FENCE_LOCK = 'kanfy-web-sign-out-fence';
const WEB_SIGN_OUT_FENCE_TTL = 24 * 60 * 60 * 1000;
const WEB_SIGN_OUT_FENCE_TIMEOUT = 2500;

interface EndedWebSession {
  identity: string;
  expiresAt: number;
}

function sessionFenceIdentity(session: Session): string | null {
  try {
    const payload = session.access_token.split('.')[1];
    if (payload) {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
        session_id?: unknown;
      };
      if (typeof decoded.session_id === 'string' && decoded.session_id) return `session:${decoded.session_id}`;
    }
  } catch {
    // Older token shapes may not carry a session id.
  }
  // Do not persist an account id or bearer token in the fence.
  return session.user?.last_sign_in_at ? `last-sign-in:${session.user.last_sign_in_at}` : null;
}

function readEndedWebSessions(required = false): EndedWebSession[] {
  try {
    const raw = localStorage.getItem(WEB_SIGN_OUT_FENCE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      if (required) throw new Error('The browser sign-out fence is malformed');
      return [];
    }
    // Readers never rewrite/prune the shared list: that would race a writer
    // even when the writer holds the fence lock. Only the locked writer prunes.
    return parsed.filter((entry): entry is EndedWebSession => {
      if (!entry || typeof entry !== 'object') return false;
      const candidate = entry as Partial<EndedWebSession>;
      return typeof candidate.identity === 'string' &&
        typeof candidate.expiresAt === 'number' && candidate.expiresAt > Date.now();
    });
  } catch (storageError) {
    if (required) throw storageError;
    logger.warn('Failed to read the browser sign-out fence', storageError);
    return [];
  }
}

/** Shared by the auth listener and storage adapter; never imports the auth client. */
export async function writeWebSignOutFence(session: Session): Promise<void> {
  const identity = sessionFenceIdentity(session);
  if (!identity) throw new Error('The browser session has no durable sign-out identity');
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), WEB_SIGN_OUT_FENCE_TIMEOUT);
  try {
    const persist = () => withBrowserAuthMutation(() => {
      const ended = readEndedWebSessions(true).filter((entry) => entry.identity !== identity);
      ended.push({ identity, expiresAt: Date.now() + WEB_SIGN_OUT_FENCE_TTL });
      // A bounded, short-lived race barrier, not session history.
      localStorage.setItem(WEB_SIGN_OUT_FENCE_KEY, JSON.stringify(ended.slice(-8)));
    }, controller.signal);
    if (locks) {
      await locks.request(WEB_SIGN_OUT_FENCE_LOCK, { mode: 'exclusive', signal: controller.signal }, persist);
    } else {
      await persist();
    }
  } finally {
    clearTimeout(timeout);
  }
}

export function isEndedWebSession(session: Session): boolean {
  const candidate = sessionFenceIdentity(session);
  return !!candidate && readEndedWebSessions().some((entry) => entry.identity === candidate);
}

export function sameWebSession(left: Session, right: Session): boolean {
  const leftIdentity = sessionFenceIdentity(left);
  const rightIdentity = sessionFenceIdentity(right);
  return leftIdentity && rightIdentity
    ? leftIdentity === rightIdentity
    : left.access_token === right.access_token;
}

/** Passive auth-js failures invalidate only the token version they used. */
export function sameWebSessionVersion(left: Session, right: Session): boolean {
  return left.access_token === right.access_token && left.refresh_token === right.refresh_token;
}

export function sessionFromStoredAuthArtifact(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const candidates = [parsed, parsed.currentSession, parsed.session,
      (parsed.data as Record<string, unknown> | undefined)?.session];
    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' &&
          typeof (candidate as Partial<Session>).access_token === 'string') {
        return candidate as Session;
      }
    }
  } catch {
    // Opaque values such as PKCE verifiers cannot be attributed to a session.
  }
  return null;
}
